/*global acalltracker */
import React, { useState, useEffect } from "react";
import { useParams, Navigate } from "react-router-dom";
import PropTypes from "prop-types";
import { Helmet } from "react-helmet";

import EstablishmentIntro from "../components/EstablishmentIntro";
import EstablishmentMap from "../components/EstablishmentMap";
import EstablishmentManagers from "../components/EstablishmentManagers";
import EstablishmentCards from "../components/EstablishmentCards";

function EstablishmentDetail(props) {
    const {
        feedBaseUrl,
        establishmentList,
        siteTitle,
        mainPageUrl,
        mapsLoaded,
        setMapsLoaded,
        locale,
        origin,
        baseName,
        isFirstLocation,
        metaImage,
        cards,
        workplacePageUrl,
        contactPageUrl,
        autoschadeRumblePageUrl,
    } = props;
    const { slug } = useParams();
    // const navigate = useNavigate();
    const establishmentItem = establishmentList.data[slug];

    if (!establishmentItem) return <Navigate to="/" replace={true} />;

    const url = `${feedBaseUrl}/api/v1/establishment/${establishmentItem.id}?l=${locale}`;
    const [establishment, setEstablishment] = useState({
        loading: true,
        data: null,
        error: false,
    });

    // eslint-disable-next-line no-unused-vars
    const [shouldTriggerPageViewEvent, SetShouldTriggerPageViewEvent] =
        useState(false);

    useEffect(() => {
        if (isFirstLocation === false) {
            SetShouldTriggerPageViewEvent(true);
        }
    }, [isFirstLocation]);

    let content = null;

    useEffect(() => {
        setEstablishment({
            loading: true,
            data: null,
            error: false,
        });
        fetch(url)
            .then((response) => response.json())
            .then((data) => {
                setEstablishment({
                    loading: false,
                    data,
                    error: data.status !== "OK",
                });
            })
            .catch(() => {
                setEstablishment({
                    loading: false,
                    data: null,
                    error: true,
                });
            });
    }, [url]);

    useEffect(() => {
        if (establishment.data === null) return;

        if (typeof acalltracker !== "undefined") {
            acalltracker.dynamicPageload();
        }
    }, [establishment]);

    if (establishment.loading) {
        content = (
            <section className="layer">
                <div className="image-content sign-inv">
                    <div className="site-width">
                        <div className="layout layout--fit layout--gutter layout--middle">
                            <div className="layout__cell unit-6-12--gt-palm unit-1-1 sign">
                                <figure className="image-content__visual visual ratio--4x3" />
                            </div>
                            <div className="layout__cell unit-5-12--desk unit-6-12--lap push-1-12--desk unit-1-1 sign">
                                {ss.i18n._t("EstablishmentApp.LOADING")}
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        );
    } else if (establishment.error) {
        content = (
            <section className="layer">
                <div className="site-width">
                    {ss.i18n._t("EstablishmentApp.ERROR")}
                    <br />
                    {`${ss.i18n._t("EstablishmentApp.ERROR_CODE")} :`}
                    {establishment.data.statusCode}
                </div>
            </section>
        );
    } else if (establishment.data.result) {
        let establishmentContent = false;

        if (establishment.data.result.content) {
            establishmentContent = document.createElement("div");
            establishmentContent.innerHTML = establishment.data.result.content;
        }

        content = (
            <div>
                <EstablishmentIntro
                    establishment={establishment.data.result}
                    workplacePageUrl={workplacePageUrl}
                    contactPageUrl={contactPageUrl}
                    autoschadeRumblePageUrl={autoschadeRumblePageUrl}
                />
                {establishment.data.result.content && (
                    <section className="layer">
                        <div className="site-width sign-xl--desk sign-l">
                            <div
                                dangerouslySetInnerHTML={{
                                    __html: establishment.data.result.content,
                                }}
                            />
                        </div>
                    </section>
                )}
                <EstablishmentMap
                    establishment={establishment.data.result}
                    mapsLoaded={mapsLoaded}
                    setMapsLoaded={setMapsLoaded}
                />
                <EstablishmentManagers
                    establishment={establishment.data.result}
                />
                {cards && <EstablishmentCards cards={cards} />}
            </div>
        );
    }

    return (
        <div>
            <Helmet>
                <title>{establishmentItem.title}</title>
                <meta property="og:site_name" content={siteTitle} />
                <meta property="og:type" content="website" />
                <meta
                    property="og:url"
                    content={origin + baseName + establishmentItem.slug}
                />
                <link
                    rel="canonical"
                    href={mainPageUrl + establishmentItem.slug}
                />

                {shouldTriggerPageViewEvent && (
                    <script>
                        {`
              window.dataLayer.push({
                'event': 'page_view',
                'page_name': '${establishmentItem.title} | ${siteTitle}',
                'page_type': 'EstablishmentPage-detail',
              });
            `}
                    </script>
                )}
            </Helmet>

            {establishment &&
                establishment.data &&
                establishment.data.result && (
                    <Helmet>
                        <meta
                            property="og:title"
                            content={
                                establishment.data.result.metaTitle ||
                                establishmentItem.title
                            }
                        />
                    </Helmet>
                )}
            {establishment &&
            establishment.data &&
            establishment.data.result &&
            establishment.data.result.metaDescription ? (
                <Helmet>
                    <meta
                        name="description"
                        content={establishment.data.result.metaDescription}
                    />
                    <meta
                        property="og:description"
                        content={establishment.data.result.metaDescription}
                    />
                </Helmet>
            ) : (
                <Helmet>
                    <meta
                        name="description"
                        content={`Bekijk hier meer informatie over de vestiging ${establishmentItem.title}. Je bent altijd welkom in onze vestiging.`}
                    />
                    <meta
                        property="og:description"
                        content={`Bekijk hier meer informatie over de vestiging ${establishmentItem.title}. Je bent altijd welkom in onze vestiging.`}
                    />
                </Helmet>
            )}

            {establishment &&
            establishment.data &&
            establishment.data.result &&
            establishment.data.result.metaImage &&
            establishment.data.result.metaImage.resize ? (
                <Helmet>
                    <meta
                        property="og:image"
                        content={establishment.data.result.metaImage.resize}
                    />
                </Helmet>
            ) : (
                <Helmet>
                    <meta property="og:image" content={metaImage} />
                </Helmet>
            )}
            {content}
        </div>
    );
}

EstablishmentDetail.propTypes = {
    feedBaseUrl: PropTypes.string.isRequired,
    establishmentList: PropTypes.object.isRequired,
    siteTitle: PropTypes.string,
    mainPageUrl: PropTypes.string.isRequired,
    mapsLoaded: PropTypes.bool.isRequired,
    setMapsLoaded: PropTypes.func.isRequired,
    locale: PropTypes.string.isRequired,
    origin: PropTypes.string.isRequired,
    baseName: PropTypes.string.isRequired,
    isFirstLocation: PropTypes.bool.isRequired,
    metaImage: PropTypes.string.isRequired,
    cards: PropTypes.object,
    workplacePageUrl: PropTypes.string,
    contactPageUrl: PropTypes.string,
    autoschadeRumblePageUrl: PropTypes.string,
};

EstablishmentDetail.defaultProps = {
    siteTitle: "Van Mossel",
    cards: {},
    workplacePageUrl: "/",
    contactPageUrl: "/",
    autoschadeRumblePageUrl: "/",
};

export default EstablishmentDetail;
